import {
  formatDate12CN,
  getPageHtml,
  getParagraphHtml,
  getReportCoverHtml,
  getSectionDateHtml,
  getSectionHtml,
  getSectionSignHtml,
  getTableHtml
} from '@/utils/generate-html/report-word'
import { queryOrganizationDict } from '@/dictionary/query-status'
import {
  inquiryReviewModelDict,
  inquiryWayDict
} from '@/dictionary/project/inquiry'
import { purchaseTypeDict } from '@/dictionary/project/common'
import { negotiationV2TypeDict } from '@/dictionary/project/negotiation-v2'
import {
  inquiryReviewTypeDict,
  onlineReviewMethodDict
} from '@/dictionary/project/online-review'
import nzh from 'nzh/cn'
import { format } from 'date-fns'
import DateUtils from '@/utils/date'
import { sortBy } from 'lodash'

// 不同采购对应不同的展示文案
const purchaseTypeObj = {
  [purchaseTypeDict.INQUIRY.value]: '询比',
  [purchaseTypeDict.NEGOTIATION.value]: '谈判'
}

/**
 * 获取报告封面
 */
const getReportCover = (
  order,
  reviewProject,
  purchaseType = purchaseType.INQUIRY.value
) => {
  /**
   * @type {ProjectCover}
   */
  const project = {
    projectName: order.orderName,
    projectCode: order.orderCode,
    ownerCompanyName: order.ownerCompanyName,
    purchaseCompanyName: order.purchaseCompanyName,
    reviewEndTime: reviewProject.reviewInfo?.reviewEndTime,
    isAgency: order.entrustType === queryOrganizationDict.ENTRUST.value
  }
  return getReportCoverHtml(
    project,
    `${purchaseTypeObj[purchaseType]}采购评审报告`
  )
}

/**
 * 获取项目简介
 */
const getProjectDescSection = (
  order,
  reviewProject,
  purchaseType = purchaseType.INQUIRY.value
) => {
  const releaseTime = formatDate12CN(reviewProject.releaseTime)
  const descList = [
    `${order.orderName}项目于${releaseTime}进行${purchaseTypeObj[purchaseType]}采购，现将有关评审情况报告如下：`
  ].map(item => getParagraphHtml(item))
  return { descList }
}

/**
 * 评审过程简介
 */
const getProcessDescSection = (
  order,
  reviewProject,
  reportType = 'default',
  isJxic,
  purchaseType = purchaseTypeDict.INQUIRY.value
) => {
  const descMap = {
    // 默认模板
    default: info => {
      return [
        `${info.orderName}项目于${info.releaseTime}在${info.noticeMedias}网站发布了${info.inquiryType}，至${info.offerEndTime}截至，共有${info.signNum}家单位参与，分别为：${info.signList}。`,
        `${info.reviewReadyTime}在有关监督人员的监督下，从专家库中随机抽取了${info.reviewerCount}名专家组成${info.reviewerCount}人的评审小组。`,
        `${info.offerEndTime}共有${info.bidderNum}家单位在${info.noticeMedias}按时递交了响应文件，分别是${info.bidders}。`,
        '监督人员对本项目抽取专家和评审工作进行了全程监督。'
      ]
    }
  }

  let noticeMediaList = ['精彩纵横云采购平台']
  if (isJxic) {
    noticeMediaList = ['江西投资集团电子采购平台']
  }

  const inquiryTypeObj = {
    [purchaseTypeDict.INQUIRY.value]: inquiryWayDict.translate(
      order.inquiryType
    ).label,
    [purchaseTypeDict.NEGOTIATION.value]: negotiationV2TypeDict.translate(
      order.competeType
    ).label
  }

  const info = {
    orderName: order.orderName,
    releaseTime: formatDate12CN(reviewProject.releaseTime),
    noticeMedias: noticeMediaList.join('、'),
    inquiryType: inquiryTypeObj[purchaseType],
    offerEndTime: formatDate12CN(order.offerEndTime),
    // 报名单位数量
    signNum: order.signupCnt,
    // 报名单位名称
    signList: order.signupSuppliers
      .replace(/,+/g, ',')
      .split(',')
      .filter(item => item)
      .join('，'),
    // 报价单位数量
    bidderNum: reviewProject.reviewSupplierList.length,
    // 报价单位名称
    bidders: reviewProject.reviewSupplierList
      .map(item => item.supplierName)
      .join('，'),
    // 专家数量
    reviewerCount: reviewProject.reviewerInfoList.length,
    // 组件专家小组时间
    reviewReadyTime: formatDate12CN(reviewProject.reviewInfo?.startTime)
  }
  const descList = descMap[reportType](info).map(item => getParagraphHtml(item))
  return { title: '评审过程简介', descList }
}

/**
 * 获取评审情况
 */
const getReviewDescSection = (
  order,
  reviewProject,
  rankList,
  reportType = 'default',
  purchaseType = purchaseTypeDict.INQUIRY.value
) => {
  const descMap = {
    default: info => {
      return [
        `评审小组根据${purchaseTypeObj[purchaseType]}文件的规定，对${info.reviewSupplierCount}家单位进行了${info.passReviewString}，${info.bidderPassCount}家单位通过了${info.passReviewString}，其响应文件有效。评审小组随后对${info.bidderPassCount}家单位的响应文件进行了详细评审。`
      ]
    }
  }
  const passReviewList = reviewProject.wayInfoList
    .filter(item => item.reviewMethod === onlineReviewMethodDict.PASS.value)
    .map(item => inquiryReviewTypeDict.translate(item.wayType)?.label)
  const info = {
    // 进行评审的单位数量
    reviewSupplierCount: reviewProject.reviewSupplierList.length,
    // 通过型评审列表名称
    passReviewString: passReviewList.join('、'),
    // 通过通过型评审的数量
    bidderPassCount: rankList.length
  }
  const descList = descMap[reportType](info).map(item => getParagraphHtml(item))
  return { title: '评审情况', descList }
}

/**
 * 获取废标情况
 */
const getWithdrawSection = (order, reviewProject) => {
  const wasteList = reviewProject.wasteList || []
  let content = getParagraphHtml('无')
  if (wasteList.length) {
    const head = [
      { content: '序号', style: 'width=50px;' },
      {
        content: '无效响应供应商名称',
        style: 'width=200px;'
      },
      {
        content: '无效响应原因',
        style: 'width=300px;'
      }
    ]
    const body = wasteList.map((item, index) => {
      return [
        { content: index + 1 },
        { content: item.bidderName },
        { content: item.wasteRemark }
      ]
    })
    content = getTableHtml(head, body)
  }
  return {
    title: '无效响应文件及其原因',
    descList: [content]
  }
}

// 获取候选人列表
function getCandidateList(list, candidateLabel) {
  return list.map(item => {
    return {
      content: `第${nzh.encodeS(item.rankNum)}${candidateLabel}：${
        item.supplierName
      }`,
      textIndent: 0
    }
  })
}

/**
 * 获取评审结果
 */
const getReviewResultSection = (
  reviewProject,
  rankList,
  reportType = 'default',
  purchaseType = purchaseTypeDict.INQUIRY.value
) => {
  // 是否是最低价
  const isRankByPrice =
    reviewProject.reviewInfo.reviewModel ===
    inquiryReviewModelDict.LOWEST_MARK.value
  const descMap = {
    default: info => {
      return [
        isRankByPrice
          ? {
            content: `评审小组“根据满足${purchaseTypeObj[purchaseType]}文件实质性响应要求且最后报价最低的原则确定成交供应商”，现推荐成交候选人排序如下：`
          }
          : {
            content: '评标委员会按综合得分的高低，现推荐成交候选人排序如下：'
          },
        ...getCandidateList(info, '成交候选人')
      ]
    }
  }
  const descList = descMap[reportType](
    sortBy(rankList || [], 'rankNum')
  ).map(item => getParagraphHtml(item.content, item.textIndent))
  return { title: '评审结论', descList }
}

/**
 * 获取评审报告内容
 */
const getReportContentHtml = (
  reportType = 'default',
  order,
  reviewProject,
  rankList,
  isJxic,
  purchaseType = purchaseTypeDict.INQUIRY.value
) => {
  // 评审简介
  const projectDescSection = getProjectDescSection(
    order,
    reviewProject,
    purchaseType
  )
  // 评审过程简介
  const processDescSection = getProcessDescSection(
    order,
    reviewProject,
    'default',
    isJxic,
    purchaseType
  )
  // 评审情况
  const reviewDescSection = getReviewDescSection(
    order,
    reviewProject,
    rankList,
    reportType,
    purchaseType
  )
  // 无效响应文件
  const withdrawSection = getWithdrawSection(order, reviewProject)
  // 评审结论
  const reviewResultSection = reviewProject.isReviewClose
    ? undefined
    : getReviewResultSection(reviewProject, rankList, reportType, purchaseType)

  const sectionSignHtml = getSectionSignHtml()

  // 评标结束日期
  const createDate = format(
    new Date(DateUtils.safeDate(reviewProject.reviewInfo.reviewEndTime)),
    'yyyy年MM月dd日'
  )
  const createDateHtml = getSectionDateHtml(createDate)
  const pageList = [
    [projectDescSection, processDescSection, reviewDescSection],
    [
      withdrawSection,
      reviewResultSection,
      sectionSignHtml,
      createDateHtml
    ].filter(value => value)
  ]
  let sectionIndex = 0
  const bodyHtml = pageList
    .map((page, pageIndex) => {
      return getPageHtml(
        pageIndex === 0 ? '评审报告' : '',
        page
          .map(section => {
            if (section.descList?.length) {
              sectionIndex = section.title ? sectionIndex + 1 : sectionIndex
              const title = section.title
                ? `${nzh.encodeS(sectionIndex)}、${section.title}`
                : ''
              return getSectionHtml(title, section.descList)
            }
            return section
          })
          .join('')
      )
    })
    .join('')

  return bodyHtml
}

const getReportBodyHtml = ({
  order,
  reviewProject,
  rankList,
  reportType = 'default',
  isJxic = false,
  purchaseType = purchaseTypeDict.INQUIRY.value
}) => {
  const coverHtml = getReportCover(order, reviewProject, purchaseType)
  const contentHtml = getReportContentHtml(
    reportType,
    order,
    reviewProject,
    rankList,
    isJxic,
    purchaseType
  )
  return `
    ${coverHtml}
    <hr class="pagebreak" noshade="noshade" size="5" style="-webkit-user-select: none; margin: 60px 0;" />
    ${contentHtml}
  `
}

const useOnlineReviewReportWord = () => {
  return {
    getReportBodyHtml
  }
}

export default useOnlineReviewReportWord
